import React, { forwardRef } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, To } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { getSize } from "src/design-system/utils/sizes";

export type IconSize = "3xs" | "2xs" | "xs" | "sm" | "md";

export type IconVariant =
  | "default"
  | "disabled"
  | "active"
  | "hover"
  | "error"
  | "warning"
  | "success"
  | "inverted";

export type IconProps = {
  icon: IconProp;
  size?: IconSize;
  variant?: IconVariant;
  dataLoc?: string;
  spin?: boolean;
};

const VARIANT_COLORS: Record<IconVariant, string> = {
  default: "text-gray-500",
  disabled: "text-gray-300",
  active: "text-indigo-500",
  hover: "text-gray-800",
  error: "text-red-500",
  warning: "text-orange-500",
  inverted: "text-white",
  success: "text-green-500",
} as const;

/**
 * Figma: https://www.figma.com/design/BO9DWO8PKNGNSI3HhpSobA/Design-System-v2?node-id=9250-9319
 */
export const Icon = forwardRef<HTMLSpanElement, IconProps>(
  ({ icon, size = "sm", variant = "default", dataLoc, spin }, ref) => {
    const { width, height } = getSize(size);

    return (
      <span ref={ref} className="flex justify-center p-1">
        <FontAwesomeIcon
          className={twJoin(
            "aspect-square transition-all",
            "cursor-[inherit]",
            width,
            height,
            VARIANT_COLORS[variant],
          )}
          data-loc={dataLoc}
          icon={icon}
          spin={spin}
        />
      </span>
    );
  },
);

const getButtonColor = (disabled: boolean, isActive: boolean): string =>
  disabled
    ? VARIANT_COLORS.disabled
    : isActive
      ? VARIANT_COLORS.active
      : twJoin(VARIANT_COLORS.default, "hover:text-gray-800");

export type IconButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "type" | "className" | "style"
> & {
  icon: IconProp;
  size?: IconSize;
  spin?: boolean;
  disabled?: boolean;
  dataLoc?: string;
  isActive?: boolean;
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      icon,
      size = "sm",
      spin = false,
      disabled = false,
      dataLoc,
      isActive = false,
      ...rest
    },
    ref,
  ) => {
    const { width, height } = getSize(size);

    return (
      <button
        ref={ref}
        disabled={disabled}
        tabIndex={0}
        type="button"
        {...rest}
      >
        <span
          className={twJoin(
            "flex justify-center p-1",
            disabled && "cursor-not-allowed",
          )}
        >
          <FontAwesomeIcon
            className={twJoin(
              "aspect-square transition-all",
              width,
              height,
              getButtonColor(disabled, isActive),
            )}
            data-loc={dataLoc}
            icon={icon}
            spin={spin}
          />
        </span>
      </button>
    );
  },
);

const getLinkColor = (isActive: boolean): string =>
  isActive
    ? VARIANT_COLORS.active
    : twJoin(VARIANT_COLORS.default, "hover:text-gray-800");

type IconLinkCommonProps = Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  "className" | "style" | "href" | "target" | "rel" | "aria-label"
> & {
  icon: IconProp;
  size?: IconSize;
  "aria-label": string;
  dataLoc?: string;
  isActive?: boolean;
  openInNewTab?: boolean;
};

type IconAnchorLinkProps = IconLinkCommonProps & {
  href: string;
  to?: never;
};

type IconRouterLinkProps = IconLinkCommonProps & {
  to: To;
  href?: never;
};

export type IconLinkProps = IconAnchorLinkProps | IconRouterLinkProps;

export const IconLink = forwardRef<HTMLAnchorElement, IconLinkProps>(
  (
    {
      icon,
      size = "sm",
      "aria-label": ariaLabel,
      dataLoc,
      isActive = false,
      openInNewTab = false,
      ...rest
    },
    ref,
  ) => {
    const { width, height } = getSize(size);

    const content = (
      <span className="flex justify-center p-1">
        <FontAwesomeIcon
          className={twJoin(
            "aspect-square transition-all",
            width,
            height,
            getLinkColor(isActive),
          )}
          data-loc={dataLoc}
          icon={icon}
        />
      </span>
    );

    const newTabProps = openInNewTab
      ? ({ target: "_blank", rel: "noopener noreferrer" } as const)
      : {};

    if ("to" in rest && rest.to) {
      const { to, ...restProps } = rest;
      return (
        <Link
          ref={ref}
          aria-label={ariaLabel}
          to={to}
          {...restProps}
          {...newTabProps}
        >
          {content}
        </Link>
      );
    }

    const { href, ...restProps } = rest as Omit<
      IconAnchorLinkProps,
      "aria-label"
    >;
    return (
      <a
        ref={ref}
        aria-label={ariaLabel}
        href={href}
        {...restProps}
        {...newTabProps}
      >
        {content}
      </a>
    );
  },
);
